// Tracking de compras terceirizadas — itens que o gerente compra de
// fornecedor externo em lote (de X em X dias) e marca em cada pedido
// quando já foram encomendados pra produção.

export const TERCEIRIZADOS = [
  { id: 'pePalito',    label: 'Pé palito',      adicionalKey: 'pePalito',    cor: '#1F2937' },
  { id: 'baseMadeira', label: 'Base de madeira', adicionalKey: 'baseMadeira', cor: '#A16207' },
  { id: 'baseMetalon', label: 'Base metalon',    adicionalKey: 'baseMetalon', cor: '#475569' },
]

// Detecta se um modelo é "industrial" (precisa de estrutura metalon
// por design, mesmo sem o adicional explícito). Heurística por palavra-chave
// no nome do modelo — "Mesa Home Office Industrial", "Mesa Industrial", etc.
export function ehModeloIndustrial(modelo) {
  return /\bindustrial\b/i.test(String(modelo || ''))
}

// Regex de detecção por texto livre — cobre descrições do Bling e
// observações livres do gerente. Casos cobertos:
//
//   baseMadeira: "base de madeira", "base madeira", "c/ base madeira",
//                "com base em madeira", "base mdf" (sem ambíguo com tampamento)
//   baseMetalon: "metalon", "base metalon", "base de metalon",
//                "estrutura metalon", "pé metalon"
//   pePalito:    "pé palito", "pe palito", "pés palito", "pés palitos"
//
const RE_BASE_MADEIRA = /\bbase\s*(?:de\s+|em\s+)?madeira\b/i
const RE_BASE_METALON = /\b(?:base\s*(?:de\s+|em\s+)?metalon|estrutura\s+metalon|\bp[éeê]s?\s+metalon|^metalon\b|\bmetalon\b)/i
const RE_PE_PALITO    = /\bp[éeê]s?\s+palitos?\b|\bpe\s*palitos?\b/i

// Verifica se algum texto livre do móvel/pedido menciona o terceirizado.
function textoMencionaTerceirizado(idTerc, ...textos) {
  const t = textos.filter(Boolean).join(' ')
  if (!t) return false
  if (idTerc === 'baseMadeira') return RE_BASE_MADEIRA.test(t)
  if (idTerc === 'baseMetalon') return RE_BASE_METALON.test(t)
  if (idTerc === 'pePalito')    return RE_PE_PALITO.test(t)
  return false
}

// Lista de IDs de terceirizados que ESTE pedido tem.
// Cascata de detecção:
//   1. Adicionais estruturados do móvel (m.adicionais.baseMadeira etc.)
//   2. Heurísticas de modelo (industrial → metalon)
//   3. Texto livre — modelo, observações do móvel, resumo do pedido
//      (cobre pedidos do Bling tipo "Roupeiro ... com base de madeira")
export function terceirizadosDoPedido(pedido) {
  const found = new Set()
  for (const m of pedido?.moveis || []) {
    // 1. Adicionais explícitos
    for (const t of TERCEIRIZADOS) {
      if (m.adicionais?.[t.adicionalKey]) found.add(t.id)
    }
    // 2. Modelo industrial → metalon
    if (ehModeloIndustrial(m.modelo)) found.add('baseMetalon')
    // 3. Texto livre: olha modelo + observações do móvel + resumo do pedido
    const textos = [m.modelo, m.observacoes, pedido.resumo]
    for (const t of TERCEIRIZADOS) {
      if (textoMencionaTerceirizado(t.id, ...textos)) found.add(t.id)
    }
  }
  return [...found]
}

// Extrai dimensões C×P×A do texto livre do modelo (caso comum quando vem
// do Bling: "Roupeiro Branco (65×50×200)", "Buffet 130×32×85", etc).
// Aceita separadores × x X *, com ou sem parênteses, com ou sem espaços.
function extrairDimensoesDoTexto(...textos) {
  const t = textos.filter(Boolean).join(' ')
  // Tenta C×P×A primeiro (3 valores), depois C×P (2 valores).
  let m = t.match(/(\d{2,4})\s*[×xX*]\s*(\d{2,4})\s*[×xX*]\s*(\d{2,4})/)
  if (m) return { c: Number(m[1]), p: Number(m[2]), a: Number(m[3]) }
  m = t.match(/(\d{2,4})\s*[×xX*]\s*(\d{2,4})/)
  if (m) return { c: Number(m[1]), p: Number(m[2]), a: 0 }
  return null
}

// Pega dimensões do móvel: primeiro tenta os campos estruturados,
// senão extrai do nome/observações/resumo.
function dimensoesDoMovel(m, pedido) {
  let c = Number(m?.comprimento)  || 0
  let p = Number(m?.profundidade) || 0
  let a = Number(m?.altura)       || 0
  if (c && p) return { c, p, a, fonte: 'campo' }
  // Fallback: parseia do texto livre
  const extr = extrairDimensoesDoTexto(m?.modelo, m?.observacoes, pedido?.resumo)
  if (extr) return { ...extr, fonte: 'texto' }
  return { c: 0, p: 0, a: 0, fonte: null }
}

// Lista os móveis específicos de um pedido que precisam de cada terceirizado,
// incluindo dimensões C×P (útil pra calcular tamanho da peça a comprar).
// Mesma cascata de detecção que terceirizadosDoPedido: adicional → heurística
// de modelo → texto livre (modelo, observações, resumo).
export function moveisQueUsam(pedido, idTerc) {
  const out = []
  for (const m of pedido?.moveis || []) {
    let usa = !!m.adicionais?.[
      TERCEIRIZADOS.find(t => t.id === idTerc)?.adicionalKey
    ]
    if (!usa && idTerc === 'baseMetalon' && ehModeloIndustrial(m.modelo)) usa = true
    if (!usa && textoMencionaTerceirizado(idTerc, m.modelo, m.observacoes, pedido.resumo)) usa = true
    if (usa) {
      const dim = dimensoesDoMovel(m, pedido)
      out.push({
        modelo:        m.modelo || '—',
        comprimento:   dim.c,
        profundidade:  dim.p,
        altura:        dim.a,
        dimFonte:      dim.fonte,  // 'campo' | 'texto' | null
      })
    }
  }
  return out
}

// True quando o tipo de terceirizado tem dimensão associada (sob medida).
export function temDimensao(idTerc) {
  return idTerc === 'baseMadeira' || idTerc === 'baseMetalon'
}

// Lista os terceirizados pendentes deste pedido (que TEM mas ainda não foram
// marcados como encomendados).
export function terceirizadosPendentes(pedido) {
  const tem = terceirizadosDoPedido(pedido)
  const enc = new Set(pedido?.terceirizadosEncomendados || [])
  return tem.filter(id => !enc.has(id))
}

// True se o pedido tem ao menos 1 terceirizado e TODOS já foram encomendados
export function todosTerceirizadosEncomendados(pedido) {
  const tem = terceirizadosDoPedido(pedido)
  if (tem.length === 0) return false
  const enc = new Set(pedido?.terceirizadosEncomendados || [])
  return tem.every(id => enc.has(id))
}

// Toggle do estado encomendado/pendente pra um terceirizado em um pedido.
// Retorna o array atualizado pra setar em terceirizadosEncomendados.
export function toggleTerceirizado(pedido, idTerc) {
  const atual = new Set(pedido?.terceirizadosEncomendados || [])
  if (atual.has(idTerc)) atual.delete(idTerc)
  else                   atual.add(idTerc)
  return [...atual]
}

// Agrega contagem de pedidos pendentes por tipo (pra widget no topo)
//   { pePalito: [pedido1, pedido2], baseMadeira: [...], baseMetalon: [...] }
// Ignora pedidos entregues / cancelados (não precisam mais comprar nada).
const STATUS_ATIVOS = new Set(['CADASTRADO', 'FABRICANDO', 'EXPEDICAO', 'ROTA', 'AJUSTE'])

export function pedidosPendentesPorTipo(pedidos, opcoes = {}) {
  const { dataAte = null } = opcoes
  const map = { pePalito: [], baseMadeira: [], baseMetalon: [] }
  for (const p of pedidos) {
    if (!STATUS_ATIVOS.has(p.status)) continue
    if (dataAte && p.prazo && p.prazo > dataAte) continue
    const pendentes = terceirizadosPendentes(p)
    for (const id of pendentes) {
      if (map[id]) map[id].push(p)
    }
  }
  // Ordena por prazo asc dentro de cada tipo
  for (const id in map) {
    map[id].sort((a, b) => (a.prazo || '~') < (b.prazo || '~') ? -1 : 1)
  }
  return map
}

export function labelTerceirizado(id) {
  return TERCEIRIZADOS.find(t => t.id === id)?.label || id
}

export function corTerceirizado(id) {
  return TERCEIRIZADOS.find(t => t.id === id)?.cor || '#6B7280'
}
